import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Box, useMediaQuery, useTheme, keyframes } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import InstrumentInfo from './components/InstrumentInfo';
import PriceInfo from './components/PriceInfo';
import MarketDepthContent from './components/MarketDepthContent';
import DesktopActions from './components/DesktopActions';
import MobileActions from './components/MobileActions';
import octopusInstance from '../../services/octopus/octopusInstance';
import { getExchangeCode } from '../../services/exchanges';
import useTouchInteraction from '../../hooks/useTouchInteraction';
import { openOrderWindow } from '../../store/actions/orders';
import { mapOrderWindowData } from '../../services/utilities';
import { getEffectiveOrderPayload } from '../../services/utilities/orderUtils';
import DeleteIcon from '@mui/icons-material/Delete';
import styled from '@emotion/styled';


const DELETE_THRESHOLD = 120; // Distance at which delete action triggers
const LOCK_AXIS_THRESHOLD = 5; // Lower threshold to detect direction faster

// Define the pulse animation
const pulseAnimation = keyframes`
  0% {
    background-color: rgba(33, 150, 243, 0.08);
  }
  50% {
    background-color: rgba(33, 150, 243, 0.2);
  }
  100% {
    background-color: rgba(33, 150, 243, 0.08);
  }
`;

// Define the dark mode pulse animation
const darkPulseAnimation = keyframes`
  0% {
    background-color: rgba(144, 202, 249, 0.16);
  }
  50% {
    background-color: rgba(144, 202, 249, 0.3);
  }
  100% {
    background-color: rgba(144, 202, 249, 0.16);
  }
`;

// Add global styles for highlighted instrument
const GlobalStyles = styled('style')({
  '@global': {
    '.highlighted-instrument': {
      animation: `${pulseAnimation} 1.5s infinite`,
      position: 'relative',
      zIndex: 1,
      '&::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(33, 150, 243, 0.04)',
        pointerEvents: 'none',
        zIndex: 0,
      },
      '.MuiTheme-dark &': {
        animation: `${darkPulseAnimation} 1.5s infinite`,
        '&::after': {
          backgroundColor: 'rgba(144, 202, 249, 0.08)',
        }
      }
    }
  }
});

const InstrumentRow = ({
  instrument,
  handleMore,
  expandedRows,
  setExpandedRows,
  marketWatches,
  watchListIndex,
  marketwatchId,
  deleteInstrument,
  isHighlighted,
  id,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down(900));
  const [isHovered, setIsHovered] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const rowRef = useRef(null);
  const [localInstrument, setLocalInstrument] = useState(instrument);
  const isExpanded = expandedRows?.includes(instrument.token);
  const formattedWatchlists = useSelector(state => state.marketwatch.formattedWatchlists);
  const { userSettings } = useSelector((state) => state.profile);
  // Add swipe state
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startY, setStartY] = useState(0);
  const [isHorizontalSwipe, setIsHorizontalSwipe] = useState(false);
  const [hasDetectedDirection, setHasDetectedDirection] = useState(false);

  // Add highlight animation state
  const [isAnimating, setIsAnimating] = useState(false);

  // Effect to handle highlight animation
  useEffect(() => {
    if (isHighlighted) {
      setIsAnimating(true);
      // Reset animation after it completes
      const timer = setTimeout(() => {
        setIsAnimating(false);
      }, 2000); // Animation duration
      return () => clearTimeout(timer);
    }
  }, [isHighlighted]);

  // Check if the instrument is already in any watchlist
  const isInMarketwatch = useCallback(() => {
    if (!formattedWatchlists || !formattedWatchlists.length) return false;

    // Check all watchlists for this instrument
    return formattedWatchlists.some(watchlist =>
      watchlist.instruments && watchlist.instruments.some(item =>
        item.token === instrument.token && item.exchange === instrument.exchange
      )
    );
  }, [formattedWatchlists, instrument]);

  // Define handleDelete first since it's used in handleTouchEnd
  const handleDelete = useCallback((e) => {
    e?.stopPropagation();
    deleteInstrument(marketwatchId, watchListIndex, marketWatches, instrument);
  }, [deleteInstrument, marketwatchId, watchListIndex, marketWatches, instrument]);

  const handleTouchStart = useCallback((e) => {
    if (!isMobile) return;
    // Remove the preventDefault to allow normal touch behavior
    setStartX(e.touches[0].clientX);
    setStartY(e.touches[0].clientY);
    setIsDragging(true);
    setHasDetectedDirection(false);
    setIsHorizontalSwipe(false);
  }, [isMobile]);

  const handleTouchMove = useCallback((e) => {
    if (!isDragging || !isMobile) return;

    const currentX = e.touches[0].clientX;
    const currentY = e.touches[0].clientY;
    const diffX = startX - currentX;
    const diffY = startY - currentY;

    // Early detection of swipe direction
    if (!hasDetectedDirection) {
      if (Math.abs(diffX) > LOCK_AXIS_THRESHOLD || Math.abs(diffY) > LOCK_AXIS_THRESHOLD) {
        const isHorizontal = Math.abs(diffX) > Math.abs(diffY);
        setIsHorizontalSwipe(isHorizontal);
        setHasDetectedDirection(true);
      }
      return;
    }

    // If it's a horizontal swipe, prevent default to stop vertical scroll on this element
    if (isHorizontalSwipe) {
      e.preventDefault(); // Only prevent default for horizontal swipes

      // Only allow left swipe (positive diff)
      if (diffX < 0) {
        setSwipeOffset(0);
        return;
      }

      // Add resistance to the swipe
      const resistance = 0.7;
      const newOffset = Math.min(diffX * resistance, DELETE_THRESHOLD);
      setSwipeOffset(newOffset);
    }

  }, [isDragging, startX, startY, isMobile, hasDetectedDirection, isHorizontalSwipe]);

  const handleTouchEnd = useCallback((e) => {
    if (!isDragging || !isMobile) return;

    setIsDragging(false);
    setHasDetectedDirection(false);
    setIsHorizontalSwipe(false);

    // Only delete if we've swiped almost all the way
    const deleteThresholdMet = swipeOffset >= (DELETE_THRESHOLD * 0.9);

    if (deleteThresholdMet) {
      handleDelete(e);
      setSwipeOffset(0);
    } else {
      // Animate back if not deleted
      setSwipeOffset(0);
    }
  }, [isDragging, swipeOffset, isMobile, handleDelete]);

  const handleTouchCancel = useCallback((e) => {
    if (!isDragging || !isMobile) return;
    setIsDragging(false);
    setHasDetectedDirection(false);
    setIsHorizontalSwipe(false);
    setSwipeOffset(0);
  }, [isDragging, isMobile]);

  const handleTap = useCallback((e) => {
    // Only handle tap if not swiping
    if (swipeOffset > 10) return;

    // Check if the click is coming from a bookmark icon or its container
    if (e.target.closest('.bookmark-icon-container') ||
      e.target.closest('.bookmark-icon') ||
      e.target.classList.contains('bookmark-icon')) {
      return;
    }

    if (isMobile) {
      setMobileDrawerOpen(true);
    } else {
      setExpandedRows((prev) =>
        prev.includes(instrument.token)
          ? prev.filter((token) => token !== instrument.token)
          : [...prev, instrument.token]
      );
    }
  }, [isMobile, instrument, setExpandedRows, swipeOffset]);

  const { handlers, touchStates } = useTouchInteraction(handleTap, isMobile);

  // Only use hover for desktop
  const handleMouseEnter = useCallback(() => {
    if (!isMobile) {
      setIsHovered(true);
    }
  }, [isMobile]);

  const handleMouseLeave = useCallback(() => {
    if (!isMobile) {
      setIsHovered(false);
    }
  }, [isMobile]);

  const handleMouseMove = useCallback(() => {
    if (!isMobile && !isHovered) {
      setIsHovered(true);
    }
  }, [isMobile, isHovered]);

  // Handle reset of hover state when the "More Actions" menu is closed
  useEffect(() => {
    if (!menuOpen) {
      const timer = setTimeout(() => {
        if (rowRef.current && !rowRef.current.matches(':hover')) {
          setIsHovered(false);
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [menuOpen]);

  useEffect(() => {
    const handler = octopusInstance.wsHandler(
      {
        messageType: "CompactMarketDataMessage",
        subscriptionLocation: `marketwatch-${marketwatchId}`,
        payload: {
          exchangeCode: getExchangeCode(instrument.exchange),
          instrumentToken: instrument.token,
        },
      });

    handler.subscribe(({ msg }) => {
      const filteredMsg = (({ ltp, absoluteChange, percentChange, ltt, bidPrice, askPrice }) => ({
        ltp,
        absoluteChange,
        percentChange,
        ltt,
        bidPrice,
        askPrice,
      }))(msg);

      setLocalInstrument((prev) => ({ ...prev, ...filteredMsg }));
    });

    // Simplified: Let the App-level visibility handler manage reconnection
    return () => handler.unsubscribe();
    // Depends on the entire instrument object reference (marketbhai dependency array)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instrument, marketwatchId]);


  useEffect(() => {
    if (!isExpanded) return;

    const handler = octopusInstance.wsHandler({
      messageType: "DetailMarketDataMessage",
      subscriptionLocation: `detail-${instrument.token}`,
      payload: {
        exchangeCode: getExchangeCode(instrument.exchange),
        instrumentToken: instrument.token,
      },
    });

    handler.subscribe(({ msg }) => {
      const filteredMsg = (({
        lastTradedTime,
        volume,
        bestBidQuantity,
        bestAskQuantity,
        bestBidPrice,
        bestAskPrice,
        openPrice,
        highPrice,
        lowPrice,
        closePrice,
        absoluteChange,
        percentChange,
      }) => ({
        lastTradedTime,
        volume,
        bestBidQuantity,
        bestAskQuantity,
        bestBidPrice,
        bestAskPrice,
        openPrice,
        highPrice,
        lowPrice,
        closePrice,
        absoluteChange,
        percentChange,
      }))(msg);

      setLocalInstrument((prev) => ({ ...prev, ...filteredMsg }));
    });

    // Simplified: Let the App-level visibility handler manage reconnection
    return () => handler.unsubscribe();
  }, [isExpanded, instrument.token]);

  const positionsArray = useSelector((state) => state.portfolio?.arrPositionsUpdateData || []);

  /* Removed local getEffectiveOrderPayload related logic as it's now imported helper function */

  const handleBuy = useCallback((localInstrument, e, userSettings) => {
    e.stopPropagation();
    dispatch(openOrderWindow(getEffectiveOrderPayload(localInstrument, 'BUY', userSettings, positionsArray)));
  }, [dispatch, positionsArray]);

  const handleSell = useCallback((localInstrument, e, userSettings) => {
    e.stopPropagation();
    dispatch(openOrderWindow(getEffectiveOrderPayload(localInstrument, 'SELL', userSettings, positionsArray)));
  }, [dispatch, positionsArray]);


  const handleMarketDepth = useCallback((e) => {
    e.stopPropagation();
    setExpandedRows((prev) =>
      prev.includes(instrument.token)
        ? prev.filter((token) => token !== instrument.token)
        : [...prev, instrument.token]
    );
  }, [instrument, setExpandedRows]);

  const handleSaveToMarketwatch = useCallback((e) => {
    e.stopPropagation();
  }, [instrument]);

  return (
    <>
      <GlobalStyles />
      <Box
        ref={rowRef}
        id={id}
        role="button"
        tabIndex={0}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchCancel}
        onClick={handleTap}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseMove={handleMouseMove}
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'flex-start',
          flexDirection: 'column',
          padding: '8px 16px',
          borderBottom: '1px solid',
          borderColor: 'divider',
          backgroundColor: isHighlighted
            ? theme.palette.mode === 'dark'
              ? 'rgba(144, 202, 249, 0.16)'
              : 'rgba(33, 150, 243, 0.08)'
            : isExpanded ? 'action.hover' : 'background.paper',
          cursor: 'pointer',
          transition: swipeOffset === 0 ? 'transform 0.2s ease-out' : 'none',
          transform: `translateX(-${swipeOffset}px)`,
          ...(isMobile ? {
            backgroundColor: touchStates.isActive && !touchStates.isTouchMoved ? 'action.hover' : 'background.paper',
            '&:active': {
              backgroundColor: !touchStates.isTouchMoved ? 'action.hover' : 'background.paper'
            }
          } : {
            '&:hover': {
              backgroundColor: 'action.hover'
            }
          }),
          WebkitTapHighlightColor: 'transparent',
          WebkitTouchCallout: 'none',
          WebkitUserSelect: 'none',
          userSelect: 'none',
          touchAction: isHorizontalSwipe ? 'pan-x' : 'pan-y',
          '& > *:not(.actions-container)': {
            pointerEvents: 'none'
          },
          animation: isAnimating
            ? `${theme.palette.mode === 'dark' ? darkPulseAnimation : pulseAnimation} 1.5s infinite`
            : 'none',
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: isHighlighted
              ? theme.palette.mode === 'dark'
                ? 'rgba(144, 202, 249, 0.08)'
                : 'rgba(33, 150, 243, 0.04)'
              : 'transparent',
            pointerEvents: 'none',
            zIndex: 0,
          }
        }}
      >
        {/* Simple Delete Icon */}
        {isMobile && swipeOffset > 0 && (
          <Box
            sx={{
              position: 'absolute',
              right: -60,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'error.main',
            }}
          >
            <DeleteIcon />
          </Box>
        )}

        <Box
          sx={{
            display: 'flex',
            width: '100%',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <InstrumentInfo instrument={localInstrument} />
          <PriceInfo instrument={localInstrument} />
        </Box>

        {isExpanded && (
          <MarketDepthContent instrument={localInstrument} />
        )}

        {!isMobile && (isHovered || menuOpen) && (
          <Box className="actions-container" sx={{ pointerEvents: 'auto' }}>
            <DesktopActions
              instrument={localInstrument}
              isExpanded={isExpanded}

              onBuy={handleBuy}
              onSell={handleSell}
              onMarketDepth={handleMarketDepth}
              onDelete={handleDelete}
              onMore={handleMore}
              onSaveToMarketwatch={handleSaveToMarketwatch}
              isInMarketwatch={isInMarketwatch()}
              userSettings={userSettings}
              onMenuStateChange={setMenuOpen}
            />
          </Box>
        )}
      </Box>

      {isMobile && (
        <MobileActions
          instrument={localInstrument}

          open={mobileDrawerOpen}
          onClose={() => {
            setMobileDrawerOpen(false);
          }}
          onBuy={(instrument, e, userSettings) => {
            e.stopPropagation();
            handleBuy(localInstrument, e, userSettings);
          }}
          onSell={(instrument, e, userSettings) => {
            e.stopPropagation();
            handleSell(localInstrument, e, userSettings);
          }}
          onMarketDepth={(e) => {
            e.stopPropagation();
            handleMarketDepth(e);
          }}
          onDelete={(e) => {
            e.stopPropagation();
            handleDelete(e);
          }}
          onMore={(e) => {
            e.stopPropagation();
            handleMore(e);
          }}
          onSaveToMarketwatch={handleSaveToMarketwatch}
          isInMarketwatch={isInMarketwatch()}
          userSettings={userSettings}
        />
      )}
    </>
  );
};

export default InstrumentRow;
